<script setup lang="ts">
import PlusIcon from '@/shared/ui/Icons/PlusFaq.vue';
import UIBreadcrumbs from '@/shared/ui/Breadcrumbs/index';
import UIAccordion from '@/shared/ui/Accordion/index';
import TelegramSupportButton from '@/widgets/telegramSupportButton/index';
import type { IBreadcrumbs } from '@/shared/ui/Breadcrumbs/types';
import type { IQuestion } from '@/pages/faq/model/types/questions';

const { t } = useI18n();
const localePath = useLocalePath();

const collapsedIndex: Ref<number> = ref(-1);

const breadcrumbs: Ref<IBreadcrumbs[]> = ref([
  { id: 1, text: t('main'), link: localePath('/') },
  { id: 2, text: t('faq'), link: '' },
]);

const questionsList: Ref<IQuestion[]> = computed(() => [
  {
    id: 1,
    title: t('faqQuestions.firstQuestionTitle'),
    answer: t('faqQuestions.firstQuestionAnswer'),
  },
  {
    id: 2,
    title: t('faqQuestions.secondQuestionTitle'),
    answer: t('faqQuestions.secondQuestionAnswer'),
  },
  {
    id: 3,
    title: t('faqQuestions.thirdQuestionTitle'),
    answer: t('faqQuestions.thirdQuestionAnswer'),
  },
  {
    id: 4,
    title: t('faqQuestions.fourthQuestionTitle'),
    answer: t('faqQuestions.fourthQuestionAnswer'),
  },
  {
    id: 5,
    title: t('faqQuestions.fifthQuestionTitle'),
    answer: t('faqQuestions.fifthQuestionAnswer'),
  },
  {
    id: 6,
    title: t('faqQuestions.sixthQuestionTitle'),
    answer: t('faqQuestions.sixthQuestionAnswer'),
  },
  {
    id: 7,
    title: t('faqQuestions.seventhQuestionTitle'),
    answer: t('faqQuestions.seventhQuestionAnswer'),
  },
  {
    id: 8,
    title: t('faqQuestions.eighthQuestionTitle'),
    answer: t('faqQuestions.eighthQuestionAnswer'),
  },
  {
    id: 9,
    title: t('faqQuestions.ninthQuestionTitle'),
    answer: t('faqQuestions.ninthQuestionAnswer'),
  },
  {
    id: 10,
    title: t('faqQuestions.tenthQuestionTitle'),
    answer: t('faqQuestions.tenthQuestionAnswer'),
  },
  {
    id: 11,
    title: t('faqQuestions.eleventhQuestionTitle'),
    answer: t('faqQuestions.eleventhQuestionAnswer'),
  },
  {
    id: 12,
    title: t('faqQuestions.twelfthQuestionTitle'),
    answer: t('faqQuestions.twelfthQuestionAnswer'),
  },
  {
    id: 13,
    title: t('faqQuestions.thirteenthQuestionTitle'),
    answer: t('faqQuestions.thirteenthQuestionAnswer'),
  },
  {
    id: 14,
    title: t('faqQuestions.fourteenthQuestionTitle'),
    answer: t('faqQuestions.fourteenthQuestionAnswer'),
  },
]);

const toggleHandler = (index: number): void => {
  collapsedIndex.value = index;
};

useSeoMeta({
  title: t('faq'),
  ogTitle: t('faq'),
  description: t('faqDescription'),
  ogDescription: t('faqDescription'),
});
</script>

<template>
  <div class="faq">
    <div class="faq__breadcrumbs">
      <UIBreadcrumbs :breadcrumbs="breadcrumbs" />
    </div>
    <section class="faq__section">
      <h1 class="faq__title">
        {{ t('faq') }}
      </h1>
      <div class="faq__content">
        <div class="faq__content-item _text">
          <ul id="collapse" ref="collapse" class="faq__list">
            <li
              v-for="(question, index) in questionsList"
              :id="`question${index + 1}`"
              :key="question.id"
              class="faq__item"
            >
              <UIAccordion
                class="faq__accordion"
                :max-height="500"
                @toggle="toggleHandler(Number(index))"
              >
                <template #accordion>
                  <div class="faq__question">
                    {{ question.title }}
                  </div>
                </template>
                <template #icon>
                  <PlusIcon class="faq__icon" />
                </template>
                <div class="faq__answer" v-html="question.answer"></div>
              </UIAccordion>
            </li>
          </ul>
        </div>
        <div class="faq__content-item _support">
          <div class="faq__text">
            {{ t('faqQuestions.supportQuestionTitle') }}
          </div>
          <div class="faq__button-wrapper">
            <TelegramSupportButton />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<style lang="less">
.faq {
  .container-d();
  .flex-column(@size-x10);

  padding-top: @size-x8;
  padding-bottom: 72px;

  &__title {
    .h1();
  }

  &__section {
    .flex-column(@size-x8);
  }

  &__content {
    display: flex;
    justify-content: space-between;
    gap: @size-x5;
  }

  &__content-item {
    width: 100%;

    &._text {
      .flex-column(@size-x6);

      max-width: 940px;
    }

    &._support {
      .flex-column(@size-x6);

      background-color: @color-athens-gray;
      padding: @size-x8 @size-x6;
      border-radius: @size-x6;
      max-width: 460px;
      height: 100%;
      position: sticky;
      top: @size-x2;
    }
  }

  &__list {
    .flex-column(@size-x3);
  }

  &__question {
    font-size: 18px;
    font-weight: 500;
    line-height: 120%;
  }

  &__answer {
    font-size: @size-x4;
    font-weight: 400;
    line-height: 130%;
    padding: 16px 24px;
  }

  &__text-wrapper {
    .flex-column(@size-x2);
  }

  &__item {
    .flex-column(@size-x3);
  }

  &__button-wrapper {
    max-width: 230px;
  }

  &__text {
    font-size: 22px;
    font-weight: 600;
    line-height: 125%;
  }

  &__accordion .accordion-panel,
  &__accordion .accordion__wrapper {
    background-color: @color-athens-gray;
  }

  &__icon {
    width: @size-x5;
    height: @size-x5;
  }

  @media @1024 {
    .container-m();

    gap: @size-x4;
    padding-top: @size-x6;
    padding-bottom: 48px;

    &__section {
      gap: @size-x6;
    }

    &__content {
      flex-direction: column;
      gap: @size-x5;
    }

    &__question {
      font-size: @size-x4;
    }

    &__answer {
      font-size: 14px;
    }

    &__content-item {
      &._support {
        .flex-column(@size-x4);

        padding: @size-x6 @size-x4;
      }
    }

    &__text {
      font-size: 18px;
    }
  }
}
</style>
